// Morphing engine for smooth page transitions and infinite scroll
import { isUserScrolling } from './scroll-manager.js';

const BLUR_ANIMATION_DURATION_MS = 1000;

/**
 * Handle appending results for infinite scroll
 */
export async function handleAppendResults(containerEl, frag, $allExistingResults) {
    const userIsScrollingBeforeAppend = isUserScrolling();
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
    
    // Save scroll position BEFORE appending
    let scrollTopBefore = 0;
    let anchorElement = null;
    let anchorViewportTop = 0;
    
    if (!userIsScrollingBeforeAppend) {
        const allResults = Array.from($allExistingResults);
        for (const result of allResults) {
            const rect = result.getBoundingClientRect();
            if (rect.top >= 0 && rect.top < window.innerHeight) {
                anchorElement = result;
                anchorViewportTop = rect.top;
                break;
            }
        }
        
        if (!anchorElement && allResults.length > 0) {
            anchorElement = allResults[0];
            anchorViewportTop = anchorElement.getBoundingClientRect().top;
        }
        
        scrollTopBefore = currentScrollTop;
    }
    
    if (!userIsScrollingBeforeAppend && anchorElement) {
        const appendLayer = document.createElement('div');
        appendLayer.className = 'morph-layer morphing-initial-append';
        appendLayer.appendChild(frag);
        containerEl.appendChild(appendLayer);
        
        const originalScrollBehavior = document.documentElement.style.scrollBehavior;
        
        // CRITICAL: Check if user is scrolling RIGHT NOW before restoring
        if (!isUserScrolling()) {
            document.documentElement.style.scrollBehavior = 'auto';
            document.documentElement.scrollTop = scrollTopBefore;
            document.body.scrollTop = scrollTopBefore;
            window.scrollTo(0, scrollTopBefore);
        }
        
        requestAnimationFrame(() => {
            appendLayer.classList.remove('morphing-initial-append');
            appendLayer.classList.add('morphing-in-append');
            
            setTimeout(() => {
                while (appendLayer.firstChild) {
                    containerEl.appendChild(appendLayer.firstChild);
                }
                if (appendLayer.parentNode) appendLayer.parentNode.removeChild(appendLayer);
                
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        // CRITICAL: Check if user is scrolling RIGHT NOW before fine-tuning
                        if (isUserScrolling()) return;
                        
                        const currentAnchorRect = anchorElement.getBoundingClientRect();
                        const curTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
                        const scrollAdjustment = currentAnchorRect.top - anchorViewportTop;
                        
                        const viewportDiff = Math.abs(currentAnchorRect.top - anchorViewportTop);
                        const scrollTopDiff = Math.abs(curTop - scrollTopBefore);
                        
                        if (viewportDiff > 5 || scrollTopDiff > 5) {
                            const fineTunedScroll = curTop + scrollAdjustment;
                            document.documentElement.scrollTop = fineTunedScroll;
                            document.body.scrollTop = fineTunedScroll;
                            window.scrollTo(0, fineTunedScroll);
                        }
                        
                        requestAnimationFrame(() => {
                            document.documentElement.style.scrollBehavior = originalScrollBehavior;
                        });
                    });
                });
            }, BLUR_ANIMATION_DURATION_MS);
        });
    } else if (!userIsScrollingBeforeAppend && scrollTopBefore > 0) {
        containerEl.appendChild(frag);
        // Fallback restoration
        if (!isUserScrolling()) {
            const originalBehavior = document.documentElement.style.scrollBehavior;
            document.documentElement.style.scrollBehavior = 'auto';
            document.documentElement.scrollTop = scrollTopBefore;
            document.body.scrollTop = scrollTopBefore;
            window.scrollTo(0, scrollTopBefore);
            requestAnimationFrame(() => {
                document.documentElement.style.scrollBehavior = originalBehavior;
            });
        }
    } else {
        containerEl.appendChild(frag);
    }
}

/**
 * Handle morphing transition for page change
 */
export function handleMorphTransition(containerEl, frag, $allExistingResults, data) {
    const firstExisting = $allExistingResults[0];
    const morphWrapper = document.createElement('div');
    morphWrapper.className = 'morph-wrapper';
    
    containerEl.insertBefore(morphWrapper, firstExisting);
    
    const oldLayer = document.createElement('div');
    oldLayer.className = 'morph-layer';
    
    $allExistingResults.each(function() { oldLayer.appendChild(this); });
    morphWrapper.appendChild(oldLayer);
    
    const newLayer = document.createElement('div');
    newLayer.className = 'morph-layer morphing-initial';
    newLayer.appendChild(frag);
    morphWrapper.appendChild(newLayer);
    
    requestAnimationFrame(() => {
        oldLayer.classList.add('morphing-out');
        newLayer.classList.remove('morphing-initial');
        newLayer.classList.add('morphing-in');
        
        setTimeout(() => {
            if (morphWrapper.parentNode === containerEl) {
                while (newLayer.firstChild) {
                    containerEl.insertBefore(newLayer.firstChild, morphWrapper);
                }
                containerEl.removeChild(morphWrapper);
            } else {
                while (newLayer.firstChild) containerEl.appendChild(newLayer.firstChild);
                if (morphWrapper.parentNode) morphWrapper.parentNode.removeChild(morphWrapper);
            }
            
            // Safety check
            const expectedCount = (data && data.results && Array.isArray(data.results)) ? data.results.length : 0;
            const $remaining = $(containerEl).find('.search-result-item');
            if ($remaining.length > expectedCount) $remaining.slice(expectedCount).remove();
        }, BLUR_ANIMATION_DURATION_MS);
    });
}
